import { Grid } from '@interstate/components/Grid';
import { Typography } from '@interstate/components/Typography';
import type { ReactNode, FC, ComponentProps, MouseEvent, KeyboardEvent } from 'react';
import { ExpandableArea } from '../../common/ExpandableArea';
import { DEAL_SUMMARY_DETAILS_EXPAND_ID, EXPANDABLE_ROOT } from '../../constants';
import { StyledExpandableAreaSection } from './DealSummaryItem.style';
import LineItemRow from './LineItemRow';
import ChevronIcon from './ChevronIcon';
import { useDispatch } from 'react-redux';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import { Action } from '@interstate/components/Action';

interface DealSummaryExpandableViewProps {
    children: ReactNode;
    containerTestId: string;
    expandableId: string;
    expandableOfferId: string;
    scopeId?: string;
    label: string;
    value: string;
    valueId: string;
    labelStyles: ComponentProps<typeof Grid>['sx'];
    valueStyles: ComponentProps<typeof Grid>['sx'];
    expandibleButtonStyles: ComponentProps<typeof Grid>['sx'];
    expandibleAreaStyles: ComponentProps<typeof Grid>['sx'];
    defaultExpanded?: boolean;
    headerAction?: ReactNode;
}

const DealSummaryExpandableView: FC<DealSummaryExpandableViewProps> = ({
    children,
    containerTestId,
    expandableId,
    expandableOfferId,
    label,
    value,
    valueId,
    labelStyles,
    expandibleButtonStyles,
    expandibleAreaStyles,
    scopeId,
    defaultExpanded,
    headerAction
}) => {
    const dispatch = useDispatch();
    const isDetailsSection = containerTestId === DEAL_SUMMARY_DETAILS_EXPAND_ID;
    const openBreakdownSlideout = (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
        e.stopPropagation();
        if (scopeId) {
            // This is to ensure that the correct tab is activated when the slideout opens
            dispatch(baseDealActionCreators.setActiveBreakdownTabDetails({ activeScopeId: scopeId }));
        }
        dispatch(baseDealActionCreators.setBreakdownSlideoutStatus(true));
    };

    return (
        <>
            <StyledExpandableAreaSection
                expandableId={isDetailsSection ? EXPANDABLE_ROOT : expandableOfferId}
                $isDetailsSection={isDetailsSection}
            >
                <LineItemRow
                    styles={{
                        pl: '0rem',
                        ml: '-0.143rem',
                        pt: '0.714rem',
                        pb: '0.714rem',
                        ...expandibleButtonStyles
                    }}
                >
                    <ChevronIcon
                        expandableId={isDetailsSection ? EXPANDABLE_ROOT : expandableOfferId}
                        isDetailsSection={isDetailsSection}
                    />
                    <Typography
                        tag="span"
                        className="summary-item-expandable-label"
                        sx={{
                            flexGrow: 1,
                            flexWrap: 'nowrap',
                            color: ({ tokens }) => tokens.ComponentActionColor,
                            ...labelStyles
                        }}
                        variant="body-xs"
                        data-testid={containerTestId}
                    >
                        {isDetailsSection ? (
                            <Action
                                size="sm"
                                data-testid="details-breakdown-slideout-btn"
                                onClick={(e) => openBreakdownSlideout(e)}
                            >
                                {label}
                            </Action>
                        ) : (
                            label
                        )}
                    </Typography>
                    <Typography
                        tag="span"
                        className="summary-item-expandable-value"
                        variant="strong-sm"
                        sx={{
                            color: ({ tokens }) => tokens.ComponentActionColor
                        }}
                        data-testid={valueId}
                    >
                        {value}
                    </Typography>
                    {headerAction}
                </LineItemRow>
            </StyledExpandableAreaSection>
            <ExpandableArea
                defaultExpanded={defaultExpanded}
                data-testid={expandableId}
                expandableId={isDetailsSection ? EXPANDABLE_ROOT : expandableOfferId}
            >
                <Grid
                    sx={{
                        pb: 2,
                        ...expandibleAreaStyles
                    }}
                >
                    {children}
                </Grid>
            </ExpandableArea>
        </>
    );
};

export default DealSummaryExpandableView;
